/**
 * /admin/cascade/l1-passed, /l2-passed, /l3-dropped.
 *
 * One component, three routes — the `stage` prop selects which slice of
 * the cascade we show:
 *   - l1         everything that survived L1 noise filter
 *   - l2         only rows where L2 actually ran AND didn't drop
 *   - l3-dropped only verdict='dropped_l3' (L3 否决的边界用例)
 *
 * Pagination: useInfiniteQuery with cursor=ts (server returns nextBefore).
 *
 * Why useInfiniteQuery and not handcrafted state: the previous version
 * stored pages in component-local state and wrote to it from inside
 * queryFn. When React Query hit cache on a key it had seen recently
 * (default staleTime=30s), queryFn never ran, so the local pages array
 * stayed empty on remount — switching back to a previously-visited
 * stage rendered an empty table until F5. useInfiniteQuery owns the
 * pages array as part of cached data, so cache hits restore the full
 * accumulated list immediately.
 */

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type CascadeTraceItem,
  type ClearCascadeTracesResponse,
  type ListCascadeTracesResponse,
  type MarkTraceAsNoiseResponse,
  Routes,
} from '@tg-leads/contracts';

import { api } from '../../lib/api';
import { toastError, toastSuccess } from '../../lib/toast';

type Stage = 'l1' | 'l2' | 'l3-dropped';

export function L1PassedTracesPage() {
  return (
    <CascadeTracesPage
      stage="l1"
      title="通过 L1 的消息"
      description={
        '这些消息通过了 L1（向量噪声过滤），但后续在 L2 / L3 被判掉、或最终成线索。' +
        '看到漏网的噪声可以一键加入噪声样本库，下一轮 L1 就会拦下。'
      }
    />
  );
}

export function L2PassedTracesPage() {
  return (
    <CascadeTracesPage
      stage="l2"
      title="通过 L2 的消息"
      description={
        '这些消息 L2 (4B 快筛) 实际跑过并给出"像线索"的判断（高置信直接出线索，' +
        '或中置信走到 L3）。L3 没出线索时尤其值得审视 — 这些是花了 4B 调用还不是线索的内容，' +
        '是高价值的噪声样本来源。'
      }
    />
  );
}

export function L3DroppedTracesPage() {
  return (
    <CascadeTracesPage
      stage="l3-dropped"
      title="L3 否决（漏判分析）"
      description={
        '这些消息 L1 + L2 都觉得"像线索"放过来了，但 L3 (14B) 最终否决了。' +
        '审视这些有两个用途：1) 看 L3 是否太严，把真线索拦下了（高 L2 信心 + 你看上去也像线索的就是误杀候选）；' +
        '2) 把里面真噪声的样本加入噪声库，下次让 L1 直接拦下，省掉 L2/L3 的 LLM 开销。'
      }
    />
  );
}

function CascadeTracesPage({
  stage,
  title,
  description,
}: {
  stage: Stage;
  title: string;
  description: string;
}) {
  const qc = useQueryClient();
  const list = useInfiniteQuery({
    queryKey: ['cascade-traces', stage],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      const qs = new URLSearchParams({ stage, limit: '50' });
      if (pageParam) qs.set('before', pageParam);
      return api.get<ListCascadeTracesResponse>(
        `${Routes.admin.cascadeTraces}?${qs}`,
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextBefore,
    // First page polls at 30s; pagination is operator-driven via the button.
    refetchInterval: 30_000,
  });

  const items: CascadeTraceItem[] = (list.data?.pages ?? []).flatMap((p) => p.items);
  const hasNext = !!list.hasNextPage;

  const clear = useMutation({
    mutationFn: () =>
      api.delete<ClearCascadeTracesResponse>(
        `${Routes.admin.cascadeTraces}?stage=${stage}`,
      ),
    onSuccess: (r) => {
      toastSuccess(`已清空 ${r.deleted} 条记录`);
      qc.invalidateQueries({ queryKey: ['cascade-traces', stage] });
    },
    onError: (e) => toastError(e),
  });
  const onClickClear = () => {
    // 只在面前真的有数据时才弹窗 —— 空列表点了等于 no-op。
    const visibleCount = items.length;
    const ok = window.confirm(
      `将清空「${title}」当前匹配的全部记录` +
        (visibleCount > 0
          ? `（界面上至少有 ${visibleCount} 条已加载，服务端可能更多）`
          : '') +
        '。\n\n本操作不影响已基于这些 trace 创建的噪声样本。\n\n确定继续？',
    );
    if (!ok) return;
    clear.mutate();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-sm text-slate-500 mt-1 whitespace-pre-line">
            {description}
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary text-xs whitespace-nowrap shrink-0 mt-1 border-rose-300 text-rose-700 hover:bg-rose-50"
          disabled={clear.isPending}
          onClick={onClickClear}
          title="清空当前列表 — 仅删除 cascade trace 记录，不删 raw_messages 也不删已创建的噪声样本"
        >
          {clear.isPending ? '清空中…' : '清空列表'}
        </button>
      </div>

      {list.isLoading ? (
        <div className="text-slate-500 text-sm">加载中…</div>
      ) : items.length === 0 ? (
        <div className="card text-sm text-slate-500">
          最近 7 天还没有匹配的记录。可能原因：还没消息进来；当前所有消息都在 L1
          被拦下了；L2 还没配置好。
        </div>
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">时间</th>
                <th className="text-left px-3 py-2">群</th>
                <th className="text-left px-3 py-2">消息文本</th>
                <th className="text-left px-3 py-2">噪声 cos</th>
                <th className="text-left px-3 py-2">L2 信心</th>
                <th className="text-left px-3 py-2">L3 信心</th>
                <th className="text-left px-3 py-2">结局</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((item) => (
                <TraceRow key={item.id} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasNext && (
        <div className="text-center">
          <button
            className="btn-secondary text-xs"
            disabled={list.isFetchingNextPage}
            onClick={() => list.fetchNextPage()}
          >
            {list.isFetchingNextPage ? '加载中…' : '加载更早的 50 条'}
          </button>
        </div>
      )}
    </div>
  );
}

function TraceRow({ item }: { item: CascadeTraceItem }) {
  const qc = useQueryClient();
  const mark = useMutation({
    mutationFn: () =>
      api.post<MarkTraceAsNoiseResponse>(
        Routes.admin.cascadeTraceMarkNoise(item.id),
        {},
      ),
    onSuccess: () => {
      toastSuccess('已加入噪声样本库，下一轮 L1 会用上');
      qc.invalidateQueries({ queryKey: ['cascade-traces'] });
    },
    onError: (e) => toastError(e),
  });
  const alreadyMarked = item.markedAsNoiseAnchorId !== null;

  return (
    <tr className={alreadyMarked ? 'opacity-50' : ''}>
      <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">
        {new Date(item.ts).toLocaleString()}
      </td>
      <td className="px-3 py-2 text-xs text-slate-700 max-w-[14rem] truncate">
        {item.groupTitle ?? '—'}
      </td>
      <td className="px-3 py-2 text-xs max-w-[28rem]">
        <div className="whitespace-pre-wrap break-words line-clamp-4">
          {item.textSnippet}
        </div>
        {item.senderUsername && (
          <div className="text-[10px] text-slate-400 mt-0.5">
            @{item.senderUsername}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-xs font-mono text-slate-600">
        {item.cosine !== null ? item.cosine.toFixed(3) : '—'}
      </td>
      <td className="px-3 py-2 text-xs font-mono text-slate-600">
        {item.fastConfidence !== null ? item.fastConfidence.toFixed(2) : '—'}
      </td>
      <td className="px-3 py-2 text-xs font-mono text-slate-600">
        {item.preciseConfidence !== null
          ? item.preciseConfidence.toFixed(2)
          : '—'}
      </td>
      <td className="px-3 py-2">
        <VerdictBadge verdict={item.verdict} />
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        {alreadyMarked ? (
          <span className="text-[11px] text-slate-400">已加入 ✓</span>
        ) : (
          <button
            className="text-[11px] px-2 py-1 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            disabled={mark.isPending}
            onClick={() => mark.mutate()}
            title="把这条消息文本作为新的噪声锚点，让下一轮 L1 把同类消息拦下"
          >
            {mark.isPending ? '保存…' : '加入噪声样本'}
          </button>
        )}
      </td>
    </tr>
  );
}

function VerdictBadge({ verdict }: { verdict: CascadeTraceItem['verdict'] }) {
  const label =
    verdict === 'dropped_l2'
      ? 'L2 否决'
      : verdict === 'dropped_l3'
        ? 'L3 否决'
        : verdict === 'lead'
          ? '成线索'
          : verdict === 'passed_l1'
            ? '过 L1'
            : '过 L2';
  const cls =
    verdict === 'lead'
      ? 'bg-emerald-100 text-emerald-800'
      : verdict === 'dropped_l2'
        ? 'bg-amber-100 text-amber-800'
        : verdict === 'dropped_l3'
          ? 'bg-rose-100 text-rose-800'
          : 'bg-slate-100 text-slate-700';
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${cls}`}
    >
      {label}
    </span>
  );
}
